import type {
    ApiPaymentService,
    CheckoutSessionService,
    PaymentInitializationParams,
    PaymentInitializationResult
} from '@/core/infrastructure/services/ApiPaymentService';

export interface InitializePaymentParams {
    intent: 'checkout' | 'installment' | 'advance' | 'payoff';
    context: {
        checkoutSessionId?: string;
        orderId?: string;
        installmentId?: string;
        amount?: string;
    };
    paymentProvider: string;
    paymentMethod: string;
    phoneNumber: string;
    idempotencyKey?: string;
    userId: string;
}

export class InitializePaymentUseCase {
    constructor(
        private readonly paymentService: ApiPaymentService,
        private readonly checkoutSessionService: CheckoutSessionService
    ) {
    }

    async execute(params: InitializePaymentParams): Promise<PaymentInitializationResult> {
        // Validate parameters based on intent
        await this.validatePaymentIntent(params);

        // Prepare payment initialization parameters
        const paymentParams: PaymentInitializationParams = {
            provider: params.paymentProvider,
            method: params.paymentMethod,
            phoneNumber: params.phoneNumber,
            intent: params.intent,
            context: params.context as any,
            idempotencyKey: params.idempotencyKey
        };

        // Initialize payment with the provider
        const paymentResult = await this.paymentService.initializePayment(paymentParams);

        // Update relevant records based on intent
        await this.updateRecordsForPaymentIntent(params, paymentResult);

        return paymentResult;
    }

    private async validatePaymentIntent(params: InitializePaymentParams): Promise<void> {
        switch (params.intent) {
            case 'checkout':
                if (!params.context.checkoutSessionId) {
                    throw new Error('Checkout session ID is required for checkout payments');
                }
                break;

            case 'installment':
            case 'advance':
            case 'payoff':
                if (!params.context.orderId) {
                    throw new Error('Order ID is required for installment payments');
                }
                break;

            default:
                throw new Error(`Invalid payment intent: ${params.intent}`);
        }
    }

    private async updateRecordsForPaymentIntent(
        params: InitializePaymentParams,
        paymentResult: PaymentInitializationResult
    ): Promise<void> {
        switch (params.intent) {
            case 'checkout':
                // Update checkout session with payment ID
                await this.checkoutSessionService.updateSession(params.context.checkoutSessionId!, {
                    paymentId: paymentResult.paymentId,
                    status: 'payment_pending'
                });
                break;

            case 'installment':
            case 'advance':
            case 'payoff':
                // These will be handled by payment completion webhooks
                // No immediate updates needed
                break;
        }
    }
}
